import { useId, useState, type ReactNode } from 'react';
import toast from 'react-hot-toast';
import { ConfirmDialog, Switch, TableCell, TableRow } from '@/components/ui';
import { getErrorMessage } from '@/api';
import type {
  AdminDecorationMethod,
  AdminProductPriceRow,
  PriceRowParams,
  ReleaseChannel,
  SalesPlatform,
} from '@/api/admin';
import { useSetProductDecorationPrice } from '@/hooks/useAdminDecorationMethods';
import { useSetProductPrices } from '@/hooks/useAdminProductPrices';
import {
  useSetProductActive,
  useSetProductEndOfLife,
} from '@/hooks/useAdminProducts';
import { priceInputToNumber } from '@/lib/price-input';
import { cn } from '@/lib/utils';
import { PriceCell } from './PriceCell';
import {
  CarryOverCell,
  ChannelReleaseCell,
  EndOfLifeCell,
  LastPriceChangeCell,
  MethodCell,
  PhoenixCell,
  ProductCell,
  priceStateView,
  SellerCostCell,
  STICKY_CELL,
  VariantsCell,
} from './ProductPriceRowCells';
import { SizePriceRow } from './SizePriceRow';

export {
  methodCellView,
  priceStateView,
  STICKY_CELL,
  type CellView,
} from './ProductPriceRowCells';

/** One decoration column: the method and its base price on the platform. */
export interface PriceColumnMethod {
  method: AdminDecorationMethod;
  basePrice: string | null;
}

/**
 * Columns before and after the decoration methods, without the release
 * channel columns (their number comes from the summary).
 */
export const FIXED_COLUMN_COUNT = 11;

const TOAST_OPTS = { position: 'bottom-right' as const };

/** "A", "A and B", "A, B and C". */
function joinNames(names: string[]): string {
  if (names.length <= 1) return names.join('');
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

export interface ProductPriceRowProps {
  row: AdminProductPriceRow;
  /** Decoration columns in catalogue order. */
  columns: PriceColumnMethod[];
  /** Admin release channels in column order (summary `releaseChannels`). */
  releaseChannels: ReleaseChannel[];
  platform: SalesPlatform;
  /** Parameters every row refresh is evaluated with. */
  priceRowParams: PriceRowParams;
  categoryName?: string;
  manufacturerName?: string;
  /** First cell: the selection checkbox, owned by the table's selection. */
  leading?: ReactNode;
}

/**
 * One product of the price table (R10, R13, R16, R20) with its expandable
 * size row. Every cell saves on its own; a success refreshes this row from
 * the server so the seller cost follows. Inactive products are dimmed but
 * stay editable. The cells live in `ProductPriceRowCells`; this component
 * owns the row state (size row, sales price confirmation) and the writes.
 */
export function ProductPriceRow({
  row,
  columns,
  releaseChannels,
  platform,
  priceRowParams,
  categoryName,
  manufacturerName,
  leading,
}: ProductPriceRowProps) {
  const sizeRowId = useId();
  const [expanded, setExpanded] = useState(false);
  const [pendingSalesPrice, setPendingSalesPrice] = useState<{
    resolve: (confirmed: boolean) => void;
  } | null>(null);

  const setActive = useSetProductActive({ priceRowParams });
  const setEndOfLife = useSetProductEndOfLife({ priceRowParams });
  const setPrices = useSetProductPrices(priceRowParams);
  const setMethodPrice = useSetProductDecorationPrice(row.id, {
    priceRowParams,
  });

  const handleToggleActive = async (isActive: boolean) => {
    try {
      const result = await setActive.mutateAsync({
        productId: row.id,
        isActive,
      });
      // Switching on from off releases the product on every admin channel.
      const released = (result.channelsReleased ?? []).map(
        (key) =>
          releaseChannels.find((channel) => channel.key === key)?.name ?? key
      );
      toast.success(
        `"${row.name}" ${isActive ? 'activated' : 'deactivated'}` +
          (released.length > 0 ? `, released on ${joinNames(released)}` : ''),
        TOAST_OPTS
      );
    } catch (error) {
      toast.error(getErrorMessage(error), TOAST_OPTS);
    }
  };

  const handleToggleEndOfLife = async (endOfLife: boolean) => {
    try {
      await setEndOfLife.mutateAsync({ productId: row.id, endOfLife });
      toast.success(
        `"${row.name}" ${endOfLife ? 'marked as end of life' : 'no longer end of life'}`,
        TOAST_OPTS
      );
    } catch (error) {
      toast.error(getErrorMessage(error), TOAST_OPTS);
    }
  };

  const commitProductPrice = async (
    field: 'purchasePrice' | 'recommendedSalesPrice',
    value: string | null
  ) => {
    await setPrices.mutateAsync({
      productId: row.id,
      input: { [field]: priceInputToNumber(value) },
    });
  };

  /** AE12: a set sales price clears the variants' own values; ask first. */
  const confirmSalesPrice = (
    value: string | null
  ): Promise<boolean> | boolean => {
    if (value === null || row.recommendedSalesPrice.count === 0) return true;
    return new Promise<boolean>((resolve) => {
      setPendingSalesPrice({ resolve });
    });
  };

  const settleSalesPrice = (confirmed: boolean) => {
    pendingSalesPrice?.resolve(confirmed);
    setPendingSalesPrice(null);
  };

  const commitMethodPrice = async (methodId: string, value: string | null) => {
    await setMethodPrice.mutateAsync({
      methodId,
      platform,
      price: priceInputToNumber(value),
    });
  };

  const purchase = priceStateView(row.purchasePrice, {
    variantCount: row.variantCount,
  });
  const sales = priceStateView(row.recommendedSalesPrice);
  const colSpan = FIXED_COLUMN_COUNT + releaseChannels.length + columns.length;

  return (
    <>
      <TableRow
        className={cn('group', !row.isActive && 'opacity-60')}
        data-product-id={row.id}
        data-inactive={!row.isActive || undefined}
      >
        <TableCell className={STICKY_CELL.select}>{leading}</TableCell>

        <TableCell className={STICKY_CELL.active}>
          <Switch
            checked={row.isActive}
            disabled={setActive.isPending}
            onCheckedChange={(checked) => void handleToggleActive(checked)}
            aria-label={`${row.name} active`}
          />
        </TableCell>

        <ProductCell
          row={row}
          categoryName={categoryName}
          manufacturerName={manufacturerName}
        />

        <PhoenixCell row={row} />

        <EndOfLifeCell
          row={row}
          disabled={setEndOfLife.isPending}
          onChange={(checked) => void handleToggleEndOfLife(checked)}
        />

        {releaseChannels.map((channel) => (
          <ChannelReleaseCell
            key={channel.key}
            row={row}
            channel={channel}
            priceRowParams={priceRowParams}
          />
        ))}

        <VariantsCell
          row={row}
          expanded={expanded}
          sizeRowId={sizeRowId}
          onToggle={() => setExpanded((open) => !open)}
        />

        <CarryOverCell row={row} />

        <TableCell>
          <PriceCell
            value={purchase.value}
            placeholder={purchase.placeholder}
            state={purchase.state}
            hint={purchase.hint}
            hintTitle={purchase.hintTitle}
            aria-label={`${row.name}, blank cost`}
            onCommit={(value) => commitProductPrice('purchasePrice', value)}
          />
        </TableCell>

        <TableCell>
          <PriceCell
            value={sales.value}
            placeholder={sales.placeholder}
            state={sales.state}
            hint={sales.hint}
            hintTitle={sales.hintTitle}
            aria-label={`${row.name}, recommended sales price`}
            onBeforeCommit={confirmSalesPrice}
            onCommit={(value) =>
              commitProductPrice('recommendedSalesPrice', value)
            }
          />
        </TableCell>

        <SellerCostCell row={row} />

        {columns.map(({ method, basePrice }) => (
          <MethodCell
            key={method.id}
            row={row}
            method={method}
            offered={row.offeredMethods.find(
              (candidate) => candidate.methodId === method.id
            )}
            platform={platform}
            basePrice={basePrice}
            priceRowParams={priceRowParams}
            onCommit={(value) => commitMethodPrice(method.id, value)}
          />
        ))}

        <LastPriceChangeCell row={row} />
      </TableRow>

      {expanded && (
        <SizePriceRow
          id={sizeRowId}
          productId={row.id}
          productName={row.name}
          colSpan={colSpan}
          priceRowParams={priceRowParams}
        />
      )}

      <ConfirmDialog
        open={pendingSalesPrice !== null}
        onOpenChange={(open) => {
          if (!open) settleSalesPrice(false);
        }}
        title="Clear variant prices?"
        description={`${row.recommendedSalesPrice.count} variant prices will be cleared. The product's sales price then applies to every variant of "${row.name}".`}
        confirmLabel="Save and clear"
        cancelLabel="Cancel"
        onConfirm={() => settleSalesPrice(true)}
      />
    </>
  );
}
